import { supabase } from '../../lib/supabase';
import type { EpisodeRow } from '../../types/database';
import type { Episode } from '../../types/index';
import type { EpisodeSearchResponse, GuestSearchResponse, SearchFilters } from '../../types/index';

function mapRowToEpisode(row: EpisodeRow): Episode {
  const published =
    (row as EpisodeRow & { published_date?: string | null }).published_date ??
    row.published_at ??
    '';
  return {
    id: row.id,
    title: row.title ?? '',
    description: row.description ?? '',
    thumbnail_url: row.thumbnail_url ?? '',
    youtube_url: row.youtube_url ?? '',
    guest_name: row.guest_name ?? undefined,
    summary: row.summary ?? undefined,
    podcast_id: row.podcast_id,
    published_date: published,
  };
}

/**
 * Get episodes for a podcast (public only).
 * Optimized: Only selects necessary columns to reduce payload size.
 */
export async function getEpisodesByPodcastId(podcastId: string): Promise<Episode[]> {
  const { data, error } = await supabase
    .from('episodes')
    .select('id,title,podcast_id,published_date,thumbnail_url,youtube_url,guest_name,description,summary')
    .eq('podcast_id', podcastId)
    .order('published_date', { ascending: false });

  if (error) {
    throw new Error(`Failed to fetch episodes: ${error.message}`);
  }

  const rows = (data ?? []) as EpisodeRow[];
  return rows.map(mapRowToEpisode);
}

/**
 * Get latest N episodes for a podcast (for carousel thumbnails).
 * Optimized: Only selects necessary columns to reduce payload size.
 */
export async function getLatestEpisodesByPodcastId(
  podcastId: string,
  limit: number = 4
): Promise<Episode[]> {
  const { data, error } = await supabase
    .from('episodes')
    .select('id,title,podcast_id,published_date,thumbnail_url,youtube_url,guest_name,description,summary')
    .eq('podcast_id', podcastId)
    .not('thumbnail_url', 'is', null)
    .order('published_date', { ascending: false })
    .limit(limit);

  if (error) {
    throw new Error(`Failed to fetch latest episodes: ${error.message}`);
  }

  const rows = (data ?? []) as EpisodeRow[];
  return rows.map(mapRowToEpisode);
}

/**
 * Get single episode by ID.
 */
export async function getEpisodeById(id: string): Promise<Episode | null> {
  const { data, error } = await supabase
    .from('episodes')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw new Error(`Failed to fetch episode: ${error.message}`);
  }

  return data ? mapRowToEpisode(data as EpisodeRow) : null;
}

export interface EpisodeWithPodcast extends Episode {
  podcast?: {
    id: string;
    title: string;
    channel_name: string;
    channel_url: string;
  };
}

/**
 * Get episode with podcast (for detail page).
 */
export async function getEpisodeWithPodcast(episodeId: string): Promise<EpisodeWithPodcast | null> {
  const { data, error } = await supabase
    .from('episodes')
    .select(
      `
      *,
      podcast:podcasts (
        id,
        title,
        channel_name,
        channel_url
      )
    `
    )
    .eq('id', episodeId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw new Error(`Failed to fetch episode: ${error.message}`);
  }

  if (!data) return null;

  const row = data as EpisodeRow & { podcast?: { id: string; title: string; channel_name: string; channel_url: string } };
  const episode = mapRowToEpisode(row);
  const published =
    (row as EpisodeRow & { published_date?: string | null }).published_date ?? row.published_at ?? '';
  return {
    ...episode,
    published_date: published,
    podcast: row.podcast,
  };
}

interface EpisodeSearchRow {
  id: string;
  title: string | null;
  description: string | null;
  podcast_id: string;
  published_date?: string | null;
  published_at?: string | null;
  thumbnail_url: string | null;
  youtube_url: string | null;
  guest_name: string | null;
  podcast?: { title: string | null } | Array<{ title: string | null }>;
}

function buildPreview(description: string | null, query: string): string {
  const text = (description ?? '').trim();
  if (!text) return '';

  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const index = lowerText.indexOf(lowerQuery);

  if (index === -1) {
    return text.length > 180 ? `${text.slice(0, 180).trim()}...` : text;
  }

  const start = Math.max(0, index - 60);
  const end = Math.min(text.length, index + lowerQuery.length + 120);
  const snippet = text.slice(start, end).trim();
  return `${start > 0 ? '...' : ''}${snippet}${end < text.length ? '...' : ''}`;
}

function detectMatchSource(row: EpisodeSearchRow, query: string): 'title' | 'guest' | 'description' {
  const lowerQuery = query.toLowerCase();
  if ((row.title ?? '').toLowerCase().includes(lowerQuery)) return 'title';
  if ((row.guest_name ?? '').toLowerCase().includes(lowerQuery)) return 'guest';
  return 'description';
}

export async function searchEpisodes(
  query: string,
  filters?: SearchFilters
): Promise<EpisodeSearchResponse> {
  const trimmedQuery = query.trim();
  if (!trimmedQuery) {
    return {
      query,
      total_results: 0,
      results: [],
      search_mode: 'metadata_only',
    };
  }

  const limit = filters?.limit ?? 20;
  let searchQuery = supabase
    .from('episodes')
    .select(
      `
      id,
      title,
      description,
      podcast_id,
      published_date,
      thumbnail_url,
      youtube_url,
      guest_name,
      podcast:podcasts (title)
    `,
      { count: 'exact' }
    )
    .or(`title.ilike.%${trimmedQuery}%,description.ilike.%${trimmedQuery}%,guest_name.ilike.%${trimmedQuery}%`)
    .order('published_date', { ascending: false })
    .limit(limit);

  if (filters?.podcastId) {
    searchQuery = searchQuery.eq('podcast_id', filters.podcastId);
  }

  const { data, error, count } = await searchQuery;

  if (error) {
    throw new Error(`Failed to search episodes: ${error.message}`);
  }

  const rows = (data ?? []) as EpisodeSearchRow[];
  const results = rows.map((row) => {
    const matchSource = detectMatchSource(row, trimmedQuery);
    const relevanceScore =
      matchSource === 'title' ? 0.95 : matchSource === 'guest' ? 0.85 : 0.7;

    return {
      episode_id: row.id,
      episode_title: row.title ?? '',
      podcast_id: row.podcast_id,
      podcast_title: Array.isArray(row.podcast)
        ? row.podcast[0]?.title ?? null
        : row.podcast?.title ?? null,
      match_type: 'metadata' as const,
      match_source: matchSource,
      relevance_score: relevanceScore,
      preview: buildPreview(row.description, trimmedQuery),
      published_date: row.published_date ?? row.published_at ?? null,
      thumbnail_url: row.thumbnail_url ?? null,
      youtube_url: row.youtube_url ?? null,
      guest_name: row.guest_name ?? null,
    };
  });

  return {
    query,
    total_results: count ?? results.length,
    results,
    search_mode: 'metadata_only',
  };
}

export async function searchEpisodesByGuest(
  guestName: string,
  filters?: Omit<SearchFilters, 'useHybrid'>
): Promise<GuestSearchResponse> {
  const trimmedGuest = guestName.trim();
  if (!trimmedGuest) {
    return {
      guest_name: guestName,
      total_results: 0,
      results: [],
    };
  }

  const limit = filters?.limit ?? 20;
  let query = supabase
    .from('episodes')
    .select(
      `
      id,
      title,
      description,
      podcast_id,
      published_date,
      thumbnail_url,
      youtube_url,
      guest_name,
      podcast:podcasts (title)
    `,
      { count: 'exact' }
    )
    .ilike('guest_name', `%${trimmedGuest}%`)
    .order('published_date', { ascending: false })
    .limit(limit);

  if (filters?.podcastId) {
    query = query.eq('podcast_id', filters.podcastId);
  }

  const { data, error, count } = await query;
  if (error) {
    throw new Error(`Failed to search episodes by guest: ${error.message}`);
  }

  const rows = (data ?? []) as EpisodeSearchRow[];
  const results = rows.map((row) => ({
    episode_id: row.id,
    episode_title: row.title ?? '',
    podcast_id: row.podcast_id,
    podcast_title: Array.isArray(row.podcast)
      ? row.podcast[0]?.title ?? null
      : row.podcast?.title ?? null,
    match_type: 'metadata' as const,
    match_source: 'guest' as const,
    relevance_score: 0.9,
    preview: buildPreview(row.description, trimmedGuest),
    published_date: row.published_date ?? row.published_at ?? null,
    thumbnail_url: row.thumbnail_url ?? null,
    youtube_url: row.youtube_url ?? null,
    guest_name: row.guest_name ?? null,
  }));

  return {
    guest_name: guestName,
    total_results: count ?? results.length,
    results,
  };
}
